import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { partnerRulesApi } from '../../services/api';
import CountrySelect from '../CountrySelect';

// Partner rules — pieces shared by the overview, the rule page and the builder (Phase 4a).
// Spec: .claude-product-specs/overlay/retailer-exception-engine-phase4-controls-PLAN.md §2, §3, §5
//
// One colour system on every screen (§5): dark grey = shown, light grey = hidden because the
// shopper was in the area, orange = hidden because we can't locate the shopper. Orange always
// marks the part a setting can change. Shown-but-unlocated (the rule says "don't exclude them")
// is a pale orange: shown, and the part the same switch would hide.
export const COLORS = {
  shown: '#374151',
  shown_unknown: '#f6c5a3',
  hidden_area: '#959595',
  hidden_unknown: '#eb7536',
};

export const BUCKET_LABELS = {
  shown: 'Shown',
  shown_unknown: "Shown · can't locate",
  hidden_area: 'Hidden · in the area',
  hidden_unknown: "Hidden · can't locate",
};

const BUCKETS = ['shown', 'shown_unknown', 'hidden_area', 'hidden_unknown'];

// The admin retailer list names a retailer `company_name`.
export const retailerName = (r) => r?.company_name || r?.display_name || r?.retailer_id;

export const fmt = (n) => (n || 0).toLocaleString('en-GB');

export const total = (b) => BUCKETS.reduce((s, k) => s + (b?.[k] || 0), 0);

export const pct = (n, t) => (t ? `${Math.round((n / t) * 1000) / 10}%` : '0%');

export const windowLabel = (detail) =>
  detail?.since_start
    ? `since ${new Date(detail.window_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : `last ${detail?.days || 7} days`;

// What step 3 would do, computed from the counter: the whole "can't locate" group moves between
// shown and hidden. The only setting whose effect is known before the change (§2).
export function projectUnknown(buckets, unknownExcluded) {
  const b = { ...buckets };
  const unknown = (b.shown_unknown || 0) + (b.hidden_unknown || 0);
  b.shown_unknown = unknownExcluded ? 0 : unknown;
  b.hidden_unknown = unknownExcluded ? unknown : 0;
  return b;
}

export function EffectBar({ buckets, height = 16, width = '100%', label }) {
  const t = total(buckets);
  const aria = `${label ? `${label}: ` : ''}${BUCKETS.filter((k) => buckets?.[k])
    .map((k) => `${pct(buckets[k], t)} ${BUCKET_LABELS[k].toLowerCase()}`)
    .join(', ') || 'no calls yet'}`;
  return (
    <Box
      role="img"
      aria-label={aria}
      sx={{ display: 'flex', height, width, borderRadius: 1, overflow: 'hidden', bgcolor: '#f1f1f1' }}
    >
      {BUCKETS.map((k) =>
        buckets?.[k] ? <Box key={k} sx={{ width: pct(buckets[k], t), bgcolor: COLORS[k] }} /> : null
      )}
    </Box>
  );
}

export function EffectLegend({ buckets, scoped = true }) {
  const t = total(buckets);
  const keys = scoped ? BUCKETS.filter((k) => k !== 'shown_unknown' || buckets?.shown_unknown) : ['hidden_area'];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5 }}>
      {keys.map((k) => (
        <Box key={k}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLORS[k] }} />
            <Typography variant="body2" color="text.secondary">
              {scoped ? BUCKET_LABELS[k] : 'Hidden'}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {fmt(buckets?.[k])} · {pct(buckets?.[k], t)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function TownList({ towns, km }) {
  if (!towns) return null;
  if (!towns.inside.length && !towns.outside.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No town list available for this place.
      </Typography>
    );
  }
  // A 27 km circle holds dozens of towns; the edge is what a retailer needs to check, so the
  // farthest towns inside lead, next to the nearest ones outside.
  const edge = towns.inside.slice(-5);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      <Box sx={{ flex: '1 1 220px' }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Inside {km} km ({towns.inside.length} towns) — farthest:
        </Typography>
        {edge.map((t) => (
          <TownRow key={`${t.name}-${t.km}`} t={t} />
        ))}
      </Box>
      <Box sx={{ flex: '1 1 220px' }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Just outside
        </Typography>
        {towns.outside.map((t) => (
          <TownRow key={`${t.name}-${t.km}`} t={t} />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
        Straight-line distance from the store to each town centre. Towns: GeoNames (CC-BY).
      </Typography>
    </Box>
  );
}

const TownRow = ({ t }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 14 }}>
    <span>{t.name}</span>
    <span style={{ fontFamily: 'monospace', color: '#666' }}>{t.km} km</span>
  </Stack>
);

// Add a store by address (§1.4): find it, show what was found with its province, save on "yes".
export function StoreFinder({ retailerId, onSaved, onCancel }) {
  const [addr, setAddr] = useState({ name: '', street: '', postal_code: '', city: '', country_code: 'NL' });
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const find = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await partnerRulesApi.lookupStore(retailerId, addr);
      if (!res.found) setError('We could not find this address. Check the street, number and postcode.');
      setFound(res.found ? res : null);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const store = await partnerRulesApi.createStore(retailerId, {
        name: addr.name || found.city,
        lat: found.lat,
        lon: found.lon,
        address_line: found.address_line,
        postal_code: found.postal_code,
        city: found.city,
        country_code: found.country_code,
        coord_source: 'geocoded',
      });
      onSaved(store);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <Stack spacing={1.5} sx={{ p: 2, bgcolor: '#f8f8f8', borderRadius: 2 }}>
      {!found ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
            <TextField label="Store name" value={addr.name} onChange={set('name')} size="small" sx={{ gridColumn: 'span 2' }} />
            <TextField label="Street and number" value={addr.street} onChange={set('street')} size="small" sx={{ gridColumn: 'span 2' }} />
            <TextField label="Postcode" value={addr.postal_code} onChange={set('postal_code')} size="small" />
            <TextField label="Town" value={addr.city} onChange={set('city')} size="small" />
            <CountrySelect value={addr.country_code} onChange={(code) => setAddr({ ...addr, country_code: code })} />
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="outlined" onClick={find} disabled={busy || !addr.street}>
              {busy ? <CircularProgress size={18} /> : 'Find the store'}
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Alert severity="info" icon={false}>
            <Typography>
              We found: {[found.address_line, found.postal_code, found.city].filter(Boolean).join(', ')}
              {found.region_name ? `, ${found.region_name}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Is this the store? The distance is measured from here ({found.lat.toFixed(5)}, {found.lon.toFixed(5)}).
            </Typography>
          </Alert>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setFound(null)}>Change address</Button>
            <Button variant="outlined" onClick={save} disabled={busy}>
              Yes, save store
            </Button>
          </Stack>
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

// The rule as one sentence, client-side, for the builder's live summary (the server writes the
// same shape in handoff_shared.partner_rules.view.describe for saved rules).
export function summarise(rule, { partnerName, storeName, regionName, ownerName }) {
  const owner = ownerName || 'the owner';
  if (!rule.who) return '';
  const whom = rule.who === 'all' ? 'any partner' : partnerName || '[partner]';
  const unknown = rule.where !== 'everywhere' && rule.unknownExcluded === true ? ", and shoppers we can't locate," : '';
  let place = '';
  if (rule.where === 'store') place = `within ${rule.km || '?'} km of ${storeName || rule.storeName || '[store]'}`;
  if (rule.where === 'region') place = `in ${regionName || rule.regionName || '[province]'}`;
  const main =
    rule.where === 'everywhere'
      ? `No shopper sees ${whom} on ${owner}'s site.`
      : `Shoppers ${place}${unknown} do not see ${whom} on ${owner}'s site.`;
  const both =
    rule.who === 'all'
      ? `partners do not show ${owner} to these shoppers either.`
      : `${partnerName || '[partner]'} does not show ${owner} to ${rule.where === 'everywhere' ? 'any shopper' : 'these shoppers'} either.`;
  return `${main} It works both ways: ${both}`;
}

// Builder steps 1-3 (§0). Controlled: `value` = { who, partner, where, storeId, km, region,
// unknownExcluded }; nothing pre-selected for step 3 (Q3).
export function RuleFields({ ownerId, ownerName, value, onChange, retailers }) {
  const [stores, setStores] = useState([]);
  const [regions, setRegions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [towns, setTowns] = useState(null);
  const v = value;
  const set = (patch) => onChange({ ...v, ...patch });

  useEffect(() => {
    if (!ownerId) return;
    partnerRulesApi.stores(ownerId).then((r) => setStores(r.stores || [])).catch(() => setStores([]));
    partnerRulesApi.regions(ownerId).then((r) => setRegions(r.regions || [])).catch(() => setRegions([]));
  }, [ownerId]);

  const store = stores.find((s) => s.id === v.storeId);
  useEffect(() => {
    if (!store || !v.km) {
      setTowns(null);
      return;
    }
    const t = setTimeout(() => {
      partnerRulesApi.towns(store.lat, store.lon, v.km).then(setTowns).catch(() => setTowns(null));
    }, 300);
    return () => clearTimeout(t);
  }, [store, v.km]);

  const partners = (retailers || []).filter((r) => r.retailer_id !== ownerId);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          1. Who do you want to exclude?
        </Typography>
        <RadioGroup
          row
          value={v.who || ''}
          onChange={(e) => set({ who: e.target.value, where: e.target.value === 'all' && v.where === 'everywhere' ? '' : v.where })}
        >
          <FormControlLabel value="all" control={<Radio />} label="Exclude all partners" />
          <FormControlLabel value="one" control={<Radio />} label="Exclude a specific partner" />
        </RadioGroup>
        {v.who === 'one' && (
          <TextField
            select
            label="Partner"
            size="small"
            value={v.partner || ''}
            onChange={(e) => set({ partner: e.target.value })}
            sx={{ minWidth: 320, mt: 1 }}
          >
            {partners.map((r) => (
              <MenuItem key={r.retailer_id} value={r.retailer_id}>
                {retailerName(r)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          2. Where?
        </Typography>
        <RadioGroup row value={v.where || ''} onChange={(e) => set({ where: e.target.value })}>
          <FormControlLabel value="store" control={<Radio />} label="Near a store" />
          <FormControlLabel value="region" control={<Radio />} label="In a province" />
          {v.who === 'one' && <FormControlLabel value="everywhere" control={<Radio />} label="Everywhere" />}
        </RadioGroup>
        {v.who === 'all' && (
          <Typography variant="body2" color="text.secondary">
            All partners everywhere would switch HandOff off for this retailer, so it is not offered.
          </Typography>
        )}

        {v.where === 'region' && (
          <TextField
            select
            label="Province"
            size="small"
            value={v.region || ''}
            onChange={(e) => set({ region: e.target.value, regionName: regions.find((r) => r.code === e.target.value)?.name })}
            sx={{ minWidth: 320, mt: 1 }}
          >
            {regions.map((r) => (
              <MenuItem key={r.code} value={r.code}>
                {r.name} ({r.code})
              </MenuItem>
            ))}
          </TextField>
        )}

        {v.where === 'store' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adding ? (
              <StoreFinder
                retailerId={ownerId}
                onCancel={() => setAdding(false)}
                onSaved={(s) => {
                  setStores([...stores, s]);
                  setAdding(false);
                  set({ storeId: s.id, storeName: s.name });
                }}
              />
            ) : (
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <TextField
                  select
                  label="Store"
                  size="small"
                  value={v.storeId || ''}
                  onChange={(e) => set({ storeId: e.target.value, storeName: stores.find((s) => s.id === e.target.value)?.name })}
                  sx={{ minWidth: 320 }}
                  helperText={stores.length ? null : 'No stores saved yet.'}
                >
                  {stores.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                      {s.address_line ? ` — ${s.address_line}, ${s.city || ''}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" size="small" onClick={() => setAdding(true)}>
                  Add a store
                </Button>
              </Stack>
            )}
            {store && (
              <>
                <TextField
                  label="Distance in km"
                  type="number"
                  size="small"
                  value={v.km || ''}
                  onChange={(e) => set({ km: Number(e.target.value) || '' })}
                  inputProps={{ min: 1, max: 100 }}
                  sx={{ maxWidth: 200 }}
                />
                <TownList towns={towns} km={v.km} />
              </>
            )}
          </Stack>
        )}
      </Box>

      {v.where && v.where !== 'everywhere' && (
        <Box>
          <Typography variant="subtitle1">3. Shoppers we can't locate</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Phone networks and VPNs often hide where a shopper is. Nothing is pre-selected: choose one.
          </Typography>
          <RadioGroup
            row
            value={v.unknownExcluded === undefined || v.unknownExcluded === null ? '' : String(v.unknownExcluded)}
            onChange={(e) => set({ unknownExcluded: e.target.value === 'true' })}
          >
            <FormControlLabel value="false" control={<Radio />} label="Don't exclude them" />
            <FormControlLabel value="true" control={<Radio />} label="Exclude them too" />
          </RadioGroup>
        </Box>
      )}

      {v.who && v.where && (
        <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f8f8f8' }}>
          <Typography variant="subtitle2" gutterBottom>
            What this rule will do
          </Typography>
          <Typography>
            {summarise(v, {
              ownerName,
              partnerName: retailerName(partners.find((r) => r.retailer_id === v.partner)),
              storeName: store?.name,
              regionName: regions.find((r) => r.code === v.region)?.name,
            })}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// value -> { payload, missing }: the API body, or null plus the first step still to do. One
// definition of "complete" for the button and the hint next to it.
export function toPayload(v) {
  const missing = (text) => ({ payload: null, missing: text });
  if (!v.who) return missing('Choose who to exclude.');
  if (v.who === 'one' && !v.partner) return missing('Choose the partner.');
  if (!v.where || (v.who === 'all' && v.where === 'everywhere')) return missing('Choose where.');
  let catchment = null;
  if (v.where === 'store') {
    if (!v.storeId) return missing('Choose or add a store.');
    if (!v.km) return missing('Enter the distance.');
    catchment = { type: 'radius', store_id: v.storeId, km: Number(v.km) };
  } else if (v.where === 'region') {
    if (!v.region) return missing('Choose the province.');
    catchment = { type: 'subdivision', codes: [v.region] };
  }
  if (catchment && typeof v.unknownExcluded !== 'boolean') {
    return missing("Choose what happens to shoppers we can't locate.");
  }
  return {
    missing: null,
    payload: {
      rule_type: v.who === 'all' ? 'catchment_exclusion' : 'partner_exclusion',
      partner_retailer_id: v.who === 'one' ? v.partner : null,
      catchment,
      ...(catchment ? { unknown_excluded: v.unknownExcluded } : {}),
    },
  };
}

// A saved rule -> builder value (for "Change").
export function fromRule(rule) {
  const c = rule.catchment;
  return {
    who: rule.rule_type === 'catchment_exclusion' ? 'all' : 'one',
    partner: rule.partner_retailer_id || '',
    where: !c ? 'everywhere' : c.type === 'radius' ? 'store' : 'region',
    storeId: c?.store_id || '',
    storeName: rule.store?.name,
    km: c?.km || '',
    region: c?.codes?.[0] || '',
    unknownExcluded: rule.unknown_excluded,
  };
}
